import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomTabs from "../../components/CustomTabs/CustomTabs";
import Deals from "../Deals/Deals";
import JointVenturesDeals from "../JointVenturesDeals/JointVenturesDeals";
import { getDealCategories } from "../../redux/slices/dealUploadSlice";
import { count } from "../../redux/slices/dealsSlice";
import { DEAL_STATUS } from "../../constants/constants";
import "./DealsManagement.scss";

/** Action Count: active / in-process deals only (same rule for IF, SCF, JV). */
const ACTION_COUNT_STATUS_IDS = new Set([
  DEAL_STATUS.PENDING.ID,
  DEAL_STATUS.APPROVED.ID,
  DEAL_STATUS.FUNDED.ID,
  DEAL_STATUS.PENDING_DISBURSEMENT.ID,
  DEAL_STATUS.DISBURSED.ID,
  DEAL_STATUS.PARTIALLY_SETTLED.ID,
]);

const getCategoryDealsTotal = (countData, categoryId) => {
  if (!categoryId || !Array.isArray(countData)) {
    return 0;
  }

  return countData
    .filter(
      (item) =>
        item.dealCategoryId === categoryId &&
        ACTION_COUNT_STATUS_IDS.has(item.statusId)
    )
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
};

const DealsManagement = () => {
  const dispatch = useDispatch();
  const location = useLocation() || {};
  const [activeTab, setActiveTab] = useState(
    location?.state?.activeTab || "deals",
  );

  const { dealCategories = [] } = useSelector(
    (state) => state?.dealUpload || {},
  );
  const { CountData = [] } = useSelector((state) => state?.deals || {});

  const invoiceFinancingCategoryId = useMemo(
    () =>
      dealCategories.find(
        (item) => item?.dealCategoryName === "Invoice Financing",
      )?.dealCategoryId || null,
    [dealCategories],
  );
  const supplyChainCategoryId = useMemo(
    () =>
      dealCategories.find(
        (item) =>
          item?.dealCategoryId === "DCT1000006" ||
          item?.dealCategoryName === "Supply Chain Financing" ||
          item?.dealCategoryName === "Supply Chain Finance",
      )?.dealCategoryId || null,
    [dealCategories],
  );
  const jointVenturesCategoryId = useMemo(
    () =>
      dealCategories.find((item) => item?.dealCategoryName === "Joint Ventures")
        ?.dealCategoryId || null,
    [dealCategories],
  );

  useEffect(() => {
    dispatch(getDealCategories());
    dispatch(count("DEALS"));
  }, [dispatch]);

  const invoiceDealsCount = useMemo(
    () => getCategoryDealsTotal(CountData, invoiceFinancingCategoryId),
    [CountData, invoiceFinancingCategoryId],
  );

  const supplyChainDealsCount = useMemo(
    () => getCategoryDealsTotal(CountData, supplyChainCategoryId),
    [CountData, supplyChainCategoryId],
  );

  const jvDealsCount = useMemo(
    () => getCategoryDealsTotal(CountData, jointVenturesCategoryId),
    [CountData, jointVenturesCategoryId],
  );

  const tabItems = useMemo(
    () => [
      {
        key: "deals",
        label: `Invoice Financing (${invoiceDealsCount})`,
        children: (
          <Deals
            dealCategoryId={invoiceFinancingCategoryId || "DCT1000001"}
            dealCategoryName="Invoice Financing"
          />
        ),
      },
      {
        key: "supply-chain-deals",
        label: `Supply Chain Financing (${supplyChainDealsCount})`,
        children: (
          <Deals
            dealCategoryId={supplyChainCategoryId || "DCT1000006"}
            dealCategoryName="Supply Chain Financing"
          />
        ),
      },
      {
        key: "joint-ventures-deals",
        label: `Joint Ventures (${jvDealsCount})`,
        children: <JointVenturesDeals />,
      },
    ],
    [
      invoiceDealsCount,
      invoiceFinancingCategoryId,
      supplyChainDealsCount,
      supplyChainCategoryId,
      jvDealsCount,
    ],
  );

  return (
    <div className="deals-management-page">
      <CustomTabs
        items={tabItems}
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyInactiveTabPane={true}
        className="deals-management-tabs"
      />
    </div>
  );
};

export default DealsManagement;
